"use client";

import { c as _c } from "react/compiler-runtime";
import { TRPCClientError } from "@trpc/client";
import { useSession } from "next-auth/react";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { projectSlice, selectIsEditable } from "#/features/project/model/projectSlice";
import { usePlaygroundSlugs } from "#/shared/hooks";
import { api } from "#/shared/lib";
import { useAppDispatch, useAppSelector } from "#/store/hooks";

/**
 * Encapsulates the project/case data fetching and side effects
 * shared between `ProjectPanel` (desktop) and `MobileCodeView` (mobile).
 */
export const useProjectPanelData = () => {
  const $ = _c(36);
  const session = useSession();
  const dispatch = useAppDispatch();
  const router = useRouter();
  const {
    projectSlug: t0,
    caseSlug: t1,
    setProject,
    clearSlugs
  } = usePlaygroundSlugs();
  const projectSlug = t0 === undefined ? "" : t0;
  const caseSlug = t1 === undefined ? "" : t1;
  const allBrief = api.project.allBrief.useQuery();
  const isEditable = useAppSelector(selectIsEditable);
  const t2 = Boolean(projectSlug);
  let t3;
  if ($[0] !== t2) {
    t3 = {
      enabled: t2,
      retry(failureCount, error) {
        if (error instanceof TRPCClientError && error.data.code === "NOT_FOUND") {
          return false;
        }
        return failureCount < 4;
      }
    };
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const selectedProject = api.project.getBySlug.useQuery(projectSlug, t3);
  let t4;
  let t5;
  if ($[2] !== dispatch || $[3] !== selectedProject.data) {
    t4 = () => {
      if (selectedProject.data) {
        dispatch(projectSlice.actions.changeProjectId(selectedProject.data.id));
      }
    };
    t5 = [selectedProject.data, dispatch];
    $[2] = dispatch;
    $[3] = selectedProject.data;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[6] !== dispatch || $[7] !== selectedProject.error) {
    t6 = () => {
      if (selectedProject.error) {
        dispatch(projectSlice.actions.loadFinish());
      }
    };
    t7 = [selectedProject.error, dispatch];
    $[6] = dispatch;
    $[7] = selectedProject.error;
    $[8] = t6;
    $[9] = t7;
  } else {
    t6 = $[8];
    t7 = $[9];
  }
  useEffect(t6, t7);
  const t8 = selectedProject.data?.id || "";
  let t9;
  if ($[10] !== caseSlug || $[11] !== t8) {
    t9 = {
      projectId: t8,
      slug: caseSlug
    };
    $[10] = caseSlug;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const t10 = Boolean(selectedProject.data?.id && caseSlug);
  let t11;
  if ($[13] !== t10) {
    t11 = {
      enabled: t10
    };
    $[13] = t10;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  const selectedCase = api.project.getCaseBySlug.useQuery(t9, t11);
  let t12;
  let t13;
  if ($[15] !== clearSlugs || $[16] !== dispatch || $[17] !== isEditable || $[18] !== selectedProject.data || $[19] !== selectedProject.error || $[20] !== session.data) {
    t12 = () => {
      if (selectedProject.error) {
        console.error("selectedProject.error: ", selectedProject.error);
        clearSlugs();
        return;
      }
      if (!selectedProject.data || !session.data) {
        if (isEditable) {
          dispatch(projectSlice.actions.changeIsEditable(false));
        }
        return;
      }
      const user = session.data.user;
      const newState = user.isAdmin || selectedProject.data.userId === user.id;
      if (isEditable !== newState) {
        dispatch(projectSlice.actions.changeIsEditable(newState));
      }
    };
    t13 = [clearSlugs, dispatch, isEditable, selectedProject.data, selectedProject.error, session.data];
    $[15] = clearSlugs;
    $[16] = dispatch;
    $[17] = isEditable;
    $[18] = selectedProject.data;
    $[19] = selectedProject.error;
    $[20] = session.data;
    $[21] = t12;
    $[22] = t13;
  } else {
    t12 = $[21];
    t13 = $[22];
  }
  useEffect(t12, t13);
  let t14;
  let t15;
  if ($[23] !== allBrief.data || $[24] !== projectSlug || $[25] !== router.isReady || $[26] !== setProject) {
    t14 = () => {
      if (allBrief.data?.length && router.isReady && !projectSlug) {
        const firstProject = allBrief.data[0];
        if (firstProject) {
          setProject(firstProject.slug, true);
        }
      }
    };
    t15 = [allBrief.data, router.isReady, projectSlug, setProject];
    $[23] = allBrief.data;
    $[24] = projectSlug;
    $[25] = router.isReady;
    $[26] = setProject;
    $[27] = t14;
    $[28] = t15;
  } else {
    t14 = $[27];
    t15 = $[28];
  }
  useEffect(t14, t15);
  let t16;
  if ($[29] !== caseSlug || $[30] !== isEditable || $[31] !== projectSlug || $[32] !== selectedCase || $[33] !== selectedProject || $[34] !== session) {
    t16 = {
      session,
      isEditable,
      selectedProject,
      selectedCase,
      projectSlug,
      caseSlug
    };
    $[29] = caseSlug;
    $[30] = isEditable;
    $[31] = projectSlug;
    $[32] = selectedCase;
    $[33] = selectedProject;
    $[34] = session;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  return t16 as const;
};